import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Phone, ExternalLink } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { useAuth } from "../lib/auth";
import { fmtShortDate } from "../lib/format";
import { Pill } from "./bits";
import NoteEditor from "./NoteEditor";

function Body({ d, onReset, confirming, setConfirming, hideProfile, onNote, readOnly }) {
  const c = d.customer;
  return (
    <>
      <div className="cp-head">
        <h2 data-testid="cp-name">{c.name}</h2>
        {c.phone ? (
          <a className="cp-phone" href={`tel:+${c.phone}`} data-testid="cp-phone">
            <Phone size={16} /> +{c.phone}
          </a>
        ) : (
          <span className="muted">Intet mobilnummer</span>
        )}
      </div>
      <div className="cp-stats">
        <div><b data-testid="cp-pickups">{c.pickups_count}</b><span>afhentninger</span></div>
        <div className={c.no_show_count ? "warn" : ""}><b data-testid="cp-noshows">{c.no_show_count}</b><span>udeblivelser</span></div>
        {c.blocked && <div className="bad"><b>Blokeret</b><span>kan ikke reservere</span></div>}
      </div>
      {readOnly ? (
        c.note ? <div className="cp-note-ro" data-testid="cp-note-readonly"><b>Note</b><p>{c.note}</p></div> : null
      ) : <NoteEditor key={c.id} cid={c.id} note={c.note} onSaved={onNote} />}
      <h3 className="cp-sub">Seneste ordrer</h3>
      <ul className="cp-orders" data-testid="cp-orders">
        {d.orders.length ? (
          d.orders.map((o) => (
            <li key={o.id}>
              <span className="muted">{fmtShortDate(o.created_at)}</span>
              <span className="cp-item">{o.qty} × {o.title}</span>
              <Pill status={o.status} label={o.status_label} />
            </li>
          ))
        ) : (
          <li className="muted">Ingen ordrer endnu</li>
        )}
      </ul>
      <div className="cp-actions">
        {!readOnly && (c.no_show_count > 0 || c.blocked) && (
          <button className={`btn${confirming ? " danger" : ""}`} onClick={() => (confirming ? onReset() : setConfirming(true))} data-testid="cp-reset">
            {confirming ? "Bekræft: nulstil udeblivelser" : "Nulstil udeblivelser"}
          </button>
        )}
        {!hideProfile && !readOnly && (
          <Link className="btn" to={`/customers?q=${encodeURIComponent(c.name)}`} data-testid="cp-profile-link">
            <ExternalLink size={16} /> Se fuld profil
          </Link>
        )}
      </div>
    </>
  );
}

export default function CustomerPanel({ cid, onClose, onChanged, hideProfile, shopName }) {
  const { store } = useAuth();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const load = () =>
    api
      .get(`/customers/${cid}`)
      .then((r) => setD(r.data))
      .catch((err) => setD({ error: apiError(err.response?.data?.detail) || "Kunden kunne ikke hentes." }));
  useEffect(() => {
    load();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  const reset = async () => {
    setConfirming(false);
    try {
      const r = await api.post(`/customers/${cid}/reset`);
      flash(r.data.flash);
      load();
      onChanged?.();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  return (
    <>
      <div className="sheet-scrim cp-scrim" onClick={onClose} data-testid="cp-scrim" />
      <aside className="sheet side cp" role="dialog" aria-label="Kunde" data-testid="customer-panel">
        <div className="grab" />
        {shopName && <div className="cp-shop" title={shopName} data-testid="cp-shop">Butik: <b>{shopName}</b></div>}
        <button className="ib-close cp-close" aria-label="Luk" onClick={onClose} data-testid="cp-close">
          <X size={20} />
        </button>
        {!d ? (
          <div className="cp-loading">
            <span className="sk sk-num" />
            <span className="sk sk-line" />
            <span className="sk sk-line" />
          </div>
        ) : d.error ? (
          <p className="notice error" data-testid="cp-error">{d.error}</p>
        ) : (
          <Body d={d} readOnly={!!store?.staff} onReset={reset} confirming={confirming} setConfirming={setConfirming} hideProfile={hideProfile}
            onNote={(note) => { setD((x) => ({ ...x, customer: { ...x.customer, note } })); onChanged?.(); }} />
        )}
      </aside>
    </>
  );
}
